import IconRecycleBinThrow from "@/components/icons/icon-recycle-bin-throw";
import type { MembershipListItemProps, UserRole } from "../types";
import Tooltip from "@/components/ui/tooltip";
import { getTeamPlanRestrictionMessage, TeamPlanRestrictionBolt } from "./team-plan-restriction";

function getNameInitials(firstName: string, lastName: string) {
	const firstInitial = firstName.charAt(0).toUpperCase();
	const lastInitial = lastName.charAt(0).toUpperCase();
	return `${firstInitial}${lastInitial}`;
}

export default function MembershipListItem({ membership, onDelete, showDeleteButton = true, blockedByPlan, userRole }: MembershipListItemProps) {
	const { id, user, role } = membership;

	// Guard against undefined user
	if (!user) {
		return null;
	}

	const { email, first_name, last_name, avatar_url } = user;

	// Check if this member is restricted (editor role when blocked by plan)
	const isRestricted = blockedByPlan && role === "editor";
	const teamRestrictionMessage = getTeamPlanRestrictionMessage(userRole);

	const roleStyles = {
		editor: "bg-blue-50 text-blue-700 border-blue-200",
		admin: "bg-purple-50 text-purple-700 border-purple-200",
	} as const;
	const currentRoleStyle = roleStyles[role as UserRole] || roleStyles.editor;

	return (
		<div className={`group flex items-center justify-between px-5 py-3 transition-colors ${isRestricted ? "bg-gray-50" : "hover:bg-gray-50"}`}>
			<div className={`flex items-center gap-3 ${isRestricted ? "opacity-50" : ""}`}>
				{avatar_url ? (
					<img src={avatar_url} alt={`${first_name} ${last_name}`} className="w-8 h-8 rounded-full object-cover" />
				) : (
					<div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center shrink-0">
						<span className="text-xs font-semibold text-brand">{getNameInitials(first_name || "", last_name || "")}</span>
					</div>
				)}

				<div className="flex flex-col min-w-0">
					<span className="text-sm font-medium text-gray-900">
						{first_name} {last_name}
					</span>
					{isRestricted ? (
						<>
							<span className="text-xs text-tertiary truncate">{email}</span>
							<span className="text-xs font-medium text-amber-700">Access paused on current plan</span>
						</>
					) : (
						<span className="text-xs text-tertiary truncate">{email}</span>
					)}
				</div>
			</div>

			<div className="flex items-center gap-2">
				{isRestricted && <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-md text-xs font-medium border border-amber-200 bg-amber-50 text-amber-700">Paused</span>}
				<span className={`inline-flex items-center justify-center w-[72px] py-1.5 rounded-md text-xs font-medium border ${currentRoleStyle} ${isRestricted ? "opacity-50" : ""}`}>
					{role.charAt(0).toUpperCase() + role.slice(1)}
				</span>

				{isRestricted && (
					<Tooltip content={teamRestrictionMessage}>
						<TeamPlanRestrictionBolt userRole={userRole} workspaceId={membership.workspace_id} />
					</Tooltip>
				)}

				{showDeleteButton && onDelete && role !== "admin" && (
					<button
						onClick={() => onDelete(id)}
						className={`p-1.5 rounded-md transition-colors text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer ${isRestricted ? "opacity-50" : ""}`}
						aria-label="Remove member"
					>
						<IconRecycleBinThrow className="w-4 h-4" />
					</button>
				)}
			</div>
		</div>
	);
}
